import {
    useState, useCallback, useRef
} from 'react';
import {
    META_MAP_DIR
} from './useFileHandler';

/**
 * Hook to manage Knowledge Builder table functionality including selections and search
 * @param selectedFolder The state of a selected folder
 * @param handleCheckMetadata The function to check metadata. Originally from useFileHandler.
 * @returns {Object} Table handlers and states
 */
export default function useKBTableHandler(selectedFolder, handleCheckMetadata) {
    const [selectedDocs, setSelectedDocs] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredDocs, setFilteredDocs] = useState(null);
    const [showSearchResultComponent, setShowSearchResultComponent] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null); // 'uploading', 'successful', 'failed', or null
    const searchName = useRef('');

    // Reset filtered docs when folder changes or search query is empty

    /**
     * Searches for documents in the current folder
     * @param {string} query - Search query
     * @returns {void}
     */
    const searchCurrentFolder = useCallback(() => {
        searchName.current = searchQuery;
        setShowSearchResultComponent(true);

        if (!selectedFolder || !searchQuery) {
            setFilteredDocs(null);

            return;
        }

        const results = Array.from(selectedFolder.keys())
            .slice(1) // Skip the META_MAP_DIR entry
            .filter(key => {
                const mapDir = selectedFolder.get(key).get(META_MAP_DIR);
                const fileName = handleCheckMetadata(mapDir, 'name');
                const isFile = handleCheckMetadata(mapDir, 'type') === 'file';

                return isFile && fileName.toLowerCase().includes(searchQuery.toLowerCase());
            });

        setFilteredDocs(results.length > 0 ? results : null);
    }, [selectedFolder, searchQuery, handleCheckMetadata]);

    /**
     * Resets the filtered docs if there are search results.
     */
    const resetFilteredDocs = () => {
        setSearchQuery('');
        setShowSearchResultComponent(false);
        setFilteredDocs(null);
        setStatusFilter(null);
    };

    /**
     * Gets the list of documents to display
     * @returns {string[]} List of document keys
     */
    const getVisibleDocs = useCallback(() => {
        if (!selectedFolder) return [];

        let docs = filteredDocs || Array.from(selectedFolder.keys()).slice(1);

        if (statusFilter) {
            docs = docs.filter(key => {
                const mapDir = selectedFolder.get(key).get(META_MAP_DIR);
                const isFile = handleCheckMetadata(mapDir, 'type') === 'file';
                if (!isFile) return false;

                // metaMap stores L1 status under key 'status' (object {status, message})
                // and L2 status under key 'l2_status'. Extract the string value.
                const l1Str = (handleCheckMetadata(mapDir, 'status')?.status || '').toLowerCase();
                const l2Str = (handleCheckMetadata(mapDir, 'l2_status')?.status || '').toLowerCase();

                switch (statusFilter) {
                    case 'uploading':
                        return l1Str === 'processing' || l1Str === 'checking' || l2Str === 'processing';
                    case 'successful':
                        // Align with CollectionNamePlate fileCounts: L1 completed or success
                        return l1Str === 'completed' || l1Str === 'success';
                    case 'failed':
                        return l1Str === 'failed' || l1Str === 'error' || l1Str === 'cancelled'
                            || l2Str === 'failed' || l2Str === 'error';
                    default:
                        return true;
                }
            });
        }

        return docs;
    }, [selectedFolder, filteredDocs, statusFilter, handleCheckMetadata]);

    // Selection handling functions
    const toggleDocSelection = useCallback((docMapDir) => {
        setSelectedDocs(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(docMapDir)) {
                newSelected.delete(docMapDir);
            } else {
                newSelected.add(docMapDir);
            }
            return newSelected;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (!selectedFolder) return;

        setSelectedDocs(prevSelected => {
            const newSelected = new Set(prevSelected);
            const visibleDocs = getVisibleDocs()
                .filter(key => {
                    const mapDir = selectedFolder.get(key).get(META_MAP_DIR);
                    return handleCheckMetadata(mapDir, 'type') === 'file';
                })
                .map(key => selectedFolder.get(key).get(META_MAP_DIR));

            const allSelected = visibleDocs.every(doc => newSelected.has(doc));
            if (allSelected) {
                visibleDocs.forEach(doc => newSelected.delete(doc));
            } else {
                visibleDocs.forEach(doc => newSelected.add(doc));
            }

            return newSelected;
        });
    }, [selectedFolder, handleCheckMetadata, getVisibleDocs]);

    const isSelected = useCallback((docMapDir) => {
        return selectedDocs.has(docMapDir);
    }, [selectedDocs]);

    const isAllSelected = useCallback(() => {
        if (!selectedFolder) return false;

        const visibleDocs = getVisibleDocs()
            .filter(key => {
                const mapDir = selectedFolder.get(key);
                if (mapDir) return handleCheckMetadata(mapDir.get(META_MAP_DIR), 'type') === 'file';
                return false;
            })
            .map(key => selectedFolder.get(key).get(META_MAP_DIR));

        return visibleDocs.length > 0 && visibleDocs.every(doc => selectedDocs.has(doc));
    }, [selectedFolder, selectedDocs, handleCheckMetadata, getVisibleDocs]);

    return {
        // Selection related
        selectedDocs,
        toggleDocSelection,
        handleSelectAll,
        isSelected,
        isAllSelected,

        // Search related
        searchQuery,
        showSearchResultComponent,
        searchName,
        resetFilteredDocs,
        setSearchQuery,
        setSelectedDocs,
        searchCurrentFolder,
        getVisibleDocs,
        setShowSearchResultComponent,

        // Status filter related
        statusFilter,
        setStatusFilter
    };
}